import { useMemo, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import {
  ArrowDown,
  ArrowLeft,
  ArrowUp,
  ArrowUpDown,
  CalendarClock,
  ChevronDown,
  ChevronRight,
  Download,
  Filter,
  Layers,
  Loader2,
  Minus,
  Package,
  PieChart as PieChartIcon,
  TrendingDown,
  TrendingUp,
  Trophy,
} from 'lucide-react'
import { ReportFilters, getDateRange } from '@/components/ReportFilters'
import type { ReportFilterValues } from '@/components/ReportFilters'
import { formatCurrency, formatDate } from '@/lib/utils'
import {
  CATEGORICAL,
  CHART_AXIS,
  CHART_COLORS,
  CHART_SERIES,
  DEFAULT_CHART_MARGIN,
  formatChartCurrency,
  GRACKLE_COLORS,
} from '@/lib/theme'
import { exportToCSV } from '@/utils/csvExport'
import { useProductByRevenue, useProductRevenueTrend } from '@/hooks/use-api'
import type { ProductRevenueFamily, ProductRevenueRow } from '@/types'
import { ChartTooltipContent } from '@/components/ui/chart-tooltip'

const pct = (v: number | null | undefined) =>
  v === null || v === undefined ? '—' : `${v.toFixed(1)}%`
const num = (v: number | null | undefined) => (v ?? 0).toLocaleString()

// CHART_SERIES has 10 brand-ratified hues (vs. 5 in CHART_COLORS), so families
// stay distinct in the donut, bar chart, table swatches and stacked trend.
const familyColor = (index: number): string =>
  CHART_SERIES[index % CHART_SERIES.length] ?? CHART_SERIES[0]

const avgDealOf = (revenue: number, wonDeals: number) => (wonDeals > 0 ? revenue / wonDeals : 0)
const perUnitOf = (revenue: number, units: number) => (units > 0 ? revenue / units : 0)

const MONTH_NAMES = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
]
// Label a 'YYYY-MM-DD' month bucket without timezone drift (parse the string
// directly rather than via Date, which would shift UTC-midnight to the prior day).
const monthLabel = (iso: string): string => {
  const [year, month] = iso.slice(0, 7).split('-')
  const idx = Number(month) - 1
  return `${MONTH_NAMES[idx] ?? month} ${year}`
}

// Local 'YYYY-MM-DD' for a Date (used for the complete-months cap; string
// comparison against filter dates is safe since both are 'YYYY-MM-DD').
const isoDate = (d: Date): string => {
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${m}-${day}`
}

type SortKey = 'name' | 'wonDeals' | 'units' | 'avgDeal' | 'perUnit' | 'revenue'
type SortDir = 'asc' | 'desc'

function Loading() {
  return (
    <div className="flex items-center justify-center py-8" role="status" aria-live="polite">
      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-hidden="true" />
      <span className="sr-only">Loading</span>
    </div>
  )
}

/** Thin horizontal bar giving a visual sense of a row's share of the grand total. */
function ShareBar({ share, color }: { share: number; color: string }) {
  const width = Math.max(0, Math.min(100, share))
  return (
    <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-muted" aria-hidden="true">
      <div className="h-full rounded-full" style={{ width: `${width}%`, backgroundColor: color }} />
    </div>
  )
}

/** Period-over-period change badge: coloured arrow + neutral percent text. */
function Delta({
  deltaAbs,
  deltaPct,
  currency,
  showAbs = false,
}: {
  deltaAbs?: number
  deltaPct?: number | null
  currency: string
  showAbs?: boolean
}) {
  if (deltaAbs === undefined) return <span className="text-muted-foreground">—</span>
  const isNew = deltaPct === null && deltaAbs > 0
  const flat = deltaAbs === 0
  const up = deltaAbs > 0
  const color = flat ? undefined : up ? CATEGORICAL.evergreen.solid : GRACKLE_COLORS.darkRaspberry
  const Icon = flat ? Minus : up ? TrendingUp : TrendingDown
  const sign = up ? '+' : '−'
  const label = isNew ? 'New' : flat ? 'No change' : `${sign}${Math.abs(deltaPct ?? 0).toFixed(1)}%`
  const srPrefix = isNew ? 'New in this period' : flat ? 'No change' : up ? 'Up' : 'Down'
  return (
    <span className="inline-flex items-center gap-1 tabular-nums">
      <Icon className="h-3.5 w-3.5 flex-shrink-0" style={{ color }} aria-hidden="true" />
      <span className="sr-only">{srPrefix} </span>
      <span>{label}</span>
      {showAbs && !isNew && !flat && (
        <span className="text-muted-foreground">
          ({sign}
          {formatCurrency(Math.abs(deltaAbs), currency)})
        </span>
      )}
    </span>
  )
}

function StatCard({
  title,
  value,
  hint,
  hintNode,
  icon: Icon,
  color,
}: {
  title: string
  value: string
  hint: string
  hintNode?: ReactNode
  icon: typeof Package
  color: string
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <Icon className="h-4 w-4" aria-hidden="true" style={{ color }} />
      </CardHeader>
      <CardContent className="p-3">
        <div className="truncate text-xl font-bold leading-none" title={value}>
          {value}
        </div>
        {hintNode ? (
          <div className="mt-0.5 text-xs leading-tight">{hintNode}</div>
        ) : (
          <p className="mt-0.5 truncate text-xs leading-tight text-muted-foreground" title={hint}>
            {hint}
          </p>
        )}
      </CardContent>
    </Card>
  )
}

/** Column header that toggles sorting; exposes aria-sort for assistive tech. */
function SortableHeader({
  label,
  columnKey,
  activeKey,
  dir,
  onSort,
  align = 'left',
}: {
  label: string
  columnKey: SortKey
  activeKey: SortKey
  dir: SortDir
  onSort: (key: SortKey) => void
  align?: 'left' | 'right'
}) {
  const active = activeKey === columnKey
  const ariaSort = active ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'
  const Icon = active ? (dir === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown
  return (
    <TableHead scope="col" aria-sort={ariaSort} className={align === 'right' ? 'text-right' : ''}>
      <button
        type="button"
        onClick={() => onSort(columnKey)}
        className={`inline-flex items-center gap-1 font-medium hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-sm ${
          align === 'right' ? 'flex-row-reverse' : ''
        } ${active ? 'text-foreground' : ''}`}
      >
        {label}
        <Icon
          className={`h-3.5 w-3.5 ${active ? '' : 'text-muted-foreground/50'}`}
          aria-hidden="true"
        />
      </button>
    </TableHead>
  )
}

/**
 * The window the report actually queries, given the selected period and the toggle.
 *
 * Pure and exported so it can be tested — it used to be three inline expressions, and
 * it was wrong in a way nobody could see. The fiscal-year preset ends on the last day
 * of February *next* year (future-inclusive, deliberately, to match the Salesforce
 * reports), and the old code replaced that end with the last day of last month whenever
 * it was later. On 2026-08-14 that silently dropped 2026-08-01 through 2027-02-28 —
 * seven months, CA$168,847.95 of won revenue — while the banner claimed it had excluded
 * "the current (partial) month".
 *
 * Order matters:
 *   1. clip the period to today. This is a report of realised revenue; it should never
 *      claim figures for months that have not happened.
 *   2. then, unless asked otherwise, pull back to the end of the last complete month so
 *      a part-finished month does not read as a collapse.
 *
 * With that order the toggle finally means what its label says: off is through the last
 * complete month, on is through today.
 */
export function resolveReportWindow({
  startDate,
  endDate,
  today,
  includeCurrentMonth,
}: {
  startDate?: string
  endDate?: string
  today: string
  includeCurrentMonth: boolean
}): {
  effectiveEndDate?: string
  capped: boolean
  endOfPrevMonth: string
  /** False when the selected period ends before last month did — the toggle is inert. */
  toggleApplies: boolean
} {
  // Last day of the previous month, derived from `today` rather than a fresh Date so
  // the function is deterministic.
  const [y, m] = today.split('-').map(Number)
  const endOfPrevMonth = isoDate(new Date(y as number, (m as number) - 1, 0))

  // "All time" (no end) is bounded by today, not left open.
  const periodEnd = endDate && endDate < today ? endDate : today

  // A period that finished before last month ended is already complete; the toggle has
  // nothing to do.
  const capApplies = !includeCurrentMonth && periodEnd > endOfPrevMonth
  // Never cap so hard the window becomes empty — "This Month" early in the month would
  // otherwise end before it starts.
  const wouldEmpty = capApplies && !!startDate && endOfPrevMonth < startDate
  const capped = capApplies && !wouldEmpty

  return {
    effectiveEndDate: capped ? endOfPrevMonth : periodEnd,
    capped,
    endOfPrevMonth,
    toggleApplies: periodEnd > endOfPrevMonth && !wouldEmpty,
  }
}

export default function ProductRevenueReport() {
  const navigate = useNavigate()
  const [filters, setFilters] = useState<ReportFilterValues>(() => getDateRange('fiscal_year'))
  const [sortKey, setSortKey] = useState<SortKey>('revenue')
  const [sortDir, setSortDir] = useState<SortDir>('desc')
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set())
  const [compareOn, setCompareOn] = useState(false)
  const [includeCurrentMonth, setIncludeCurrentMonth] = useState(false)

  const window = useMemo(
    () =>
      resolveReportWindow({
        startDate: filters.startDate,
        endDate: filters.endDate,
        today: isoDate(new Date()),
        includeCurrentMonth,
      }),
    [filters.startDate, filters.endDate, includeCurrentMonth]
  )
  const { effectiveEndDate, capped, endOfPrevMonth, toggleApplies } = window

  // Comparison needs an explicit, bounded range — there is no "prior period" for
  // the All-Time view, so the toggle is disabled unless both dates are set.
  const canCompare = !!filters.startDate && !!effectiveEndDate
  const compare = compareOn && canCompare

  const params = {
    startDate: filters.startDate,
    endDate: effectiveEndDate,
    owner_id: filters.owner_id,
    compare,
  }

  const report = useProductByRevenue(params)
  const data = report.data
  const currency = data?.currency ?? 'CAD'
  const hasData = !!data && data.families.length > 0
  const comparing = !!data?.compare

  const trendQuery = useProductRevenueTrend({
    startDate: filters.startDate,
    endDate: effectiveEndDate,
    owner_id: filters.owner_id,
  })

  const handleSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortKey(key)
      setSortDir(key === 'name' ? 'asc' : 'desc')
    }
  }

  const toggleFamily = (family: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev)
      if (next.has(family)) next.delete(family)
      else next.add(family)
      return next
    })
  }

  // Families + products sorted by the active column. Families are ordered by
  // their aggregate of the same metric so the grouping stays coherent.
  const sortedFamilies = useMemo(() => {
    if (!data) return []
    const factor = sortDir === 'asc' ? 1 : -1
    const cmp = (a: number | string, b: number | string) =>
      (typeof a === 'string' ? a.localeCompare(b as string) : a - (b as number)) * factor

    const productVal = (p: ProductRevenueRow): number | string => {
      switch (sortKey) {
        case 'name':
          return p.name.toLowerCase()
        case 'wonDeals':
          return p.wonDeals
        case 'units':
          return p.units
        case 'avgDeal':
          return avgDealOf(p.revenue, p.wonDeals)
        case 'perUnit':
          return perUnitOf(p.revenue, p.units)
        default:
          return p.revenue
      }
    }

    const familyVal = (f: ProductRevenueFamily): number | string => {
      const deals = f.products.reduce((s, p) => s + p.wonDeals, 0)
      const units = f.products.reduce((s, p) => s + p.units, 0)
      switch (sortKey) {
        case 'name':
          return f.family.toLowerCase()
        case 'wonDeals':
          return deals
        case 'units':
          return units
        case 'avgDeal':
          return avgDealOf(f.subtotal, deals)
        case 'perUnit':
          return perUnitOf(f.subtotal, units)
        default:
          return f.subtotal
      }
    }

    return data.families
      .map((f) => ({
        ...f,
        products: [...f.products].sort((a, b) => cmp(productVal(a), productVal(b))),
      }))
      .sort((a, b) => cmp(familyVal(a), familyVal(b)))
  }, [data, sortKey, sortDir])

  // Family colours are keyed by the *default* (revenue) ordering so the donut,
  // bar chart and table swatches stay consistent regardless of table sort.
  const colorByFamily = useMemo(() => {
    const map = new Map<string, string>()
    ;(data?.families ?? []).forEach((f, index) => map.set(f.family, familyColor(index)))
    return map
  }, [data])

  const familyChartData = useMemo(
    () =>
      (data?.families ?? []).map((fam) => ({
        name: fam.family,
        value: fam.subtotal,
        share: fam.share,
        color: colorByFamily.get(fam.family) ?? CHART_COLORS[0],
      })),
    [data, colorByFamily]
  )

  const allProducts = useMemo(
    () =>
      (data?.families ?? []).flatMap((fam) =>
        fam.products.map((p) => ({ ...p, family: fam.family }))
      ),
    [data]
  )

  const topProducts = useMemo(
    () =>
      [...allProducts]
        .sort((a, b) => b.revenue - a.revenue)
        .slice(0, 10)
        .map((p) => ({
          name: p.name,
          revenue: p.revenue,
          share: p.share,
          color: colorByFamily.get(p.family) ?? CHART_COLORS[0],
        })),
    [allProducts, colorByFamily]
  )

  const productCount = allProducts.length

  // Pivot monthly trend points into one row per month keyed by family name,
  // zero-filling absent families so stacked areas stay continuous.
  const trend = trendQuery.data
  const trendData = useMemo(() => {
    if (!trend) return { rows: [] as Record<string, number | string>[], families: [] as string[] }
    const byMonth = new Map<string, Record<string, number | string>>()
    for (const pt of trend.points) {
      const label = monthLabel(pt.month)
      let row = byMonth.get(label)
      if (!row) {
        row = { month: label }
        byMonth.set(label, row)
      }
      row[pt.family] = ((row[pt.family] as number) ?? 0) + pt.revenue
    }
    const rows = Array.from(byMonth.values())
    for (const row of rows) {
      for (const f of trend.families) if (row[f] === undefined) row[f] = 0
    }
    return { rows, families: trend.families }
  }, [trend])

  // Revenue concentration: how much of total revenue the top few products carry.
  const concentration = useMemo(() => {
    const sorted = [...allProducts].sort((a, b) => b.revenue - a.revenue)
    const topN = Math.min(3, sorted.length)
    const shareSum = sorted.slice(0, topN).reduce((s, p) => s + (p.share ?? 0), 0)
    return { topN, shareSum }
  }, [allProducts])

  const allCollapsed = hasData && collapsed.size === data!.families.length

  const handleExportCSV = () => {
    if (!data) return
    const rows = data.families.flatMap((fam) =>
      fam.products.map((p) => ({
        Family: fam.family,
        Product: p.name,
        Code: p.productCode || '',
        'Won Deals': p.wonDeals,
        Units: p.units,
        'Avg Deal': avgDealOf(p.revenue, p.wonDeals).toFixed(2),
        'Per Unit': perUnitOf(p.revenue, p.units).toFixed(2),
        Revenue: p.revenue,
        '% of Total': `${(p.share ?? 0).toFixed(1)}%`,
        ...(comparing
          ? {
              'Prior Revenue': (p.priorRevenue ?? 0).toFixed(2),
              'Change ($)': (p.deltaAbs ?? 0).toFixed(2),
              'Change (%)':
                p.deltaPct === null || p.deltaPct === undefined
                  ? 'New'
                  : `${p.deltaPct.toFixed(1)}%`,
            }
          : {}),
      }))
    )
    exportToCSV({ data: rows, filename: 'product-revenue.csv' })
  }

  const barChartHeight = Math.max(200, topProducts.length * 34 + 24)

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/reports')}
            aria-label="Back to reports"
          >
            <ArrowLeft className="mr-2 h-4 w-4" aria-hidden="true" />
            Reports
          </Button>
          <div>
            <h1 className="text-2xl font-semibold">Product by Revenue</h1>
            <p className="text-sm text-muted-foreground">
              Realized (Closed-Won) revenue by product, grouped by family. Values in CAD, based on
              won-opportunity line items. Date range applies to the opportunity close date.
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            onClick={() => setIncludeCurrentMonth((v) => !v)}
            variant={includeCurrentMonth ? 'default' : 'outline'}
            disabled={!toggleApplies}
            aria-pressed={includeCurrentMonth}
            title={
              !toggleApplies
                ? 'The selected range already ends before the current month'
                : includeCurrentMonth
                  ? 'Showing through today, including the current partial month'
                  : 'Showing complete months only (through the end of last month). Click to include the current partial month.'
            }
          >
            <CalendarClock className="mr-2 h-4 w-4" aria-hidden="true" />
            Include current month
          </Button>
          <Button
            onClick={() => setCompareOn((v) => !v)}
            variant={compare ? 'default' : 'outline'}
            disabled={!canCompare}
            aria-pressed={compare}
            title={
              canCompare
                ? 'Compare against the prior equal-length period'
                : 'Select a start and end date to compare against a prior period'
            }
          >
            <TrendingUp className="mr-2 h-4 w-4" aria-hidden="true" />
            Compare to prior period
          </Button>
          <Button
            onClick={handleExportCSV}
            variant="outline"
            disabled={!hasData}
            aria-label="Export product revenue to CSV"
          >
            <Download className="mr-2 h-4 w-4" aria-hidden="true" />
            Export CSV
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center gap-2 pb-2">
          <Filter className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          <CardTitle className="text-base">Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <ReportFilters value={filters} onChange={setFilters} defaultPeriod="fiscal_year" />
        </CardContent>
      </Card>

      {report.isLoading ? (
        <Card>
          <CardContent>
            <Loading />
          </CardContent>
        </Card>
      ) : !hasData ? (
        <Card>
          <CardContent>
            <p className="py-8 text-center text-sm text-muted-foreground">
              No won product revenue in this period.
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Summary stat cards */}
          <div className="grid gap-4 md:grid-cols-4">
            <StatCard
              title="Total Revenue"
              value={formatCurrency(data!.grandTotal, currency)}
              hint="Closed-Won deal value, CAD"
              hintNode={
                comparing ? (
                  <span className="flex flex-col gap-0.5">
                    <Delta
                      deltaAbs={data!.grandDeltaAbs}
                      deltaPct={data!.grandDeltaPct}
                      currency={currency}
                      showAbs
                    />
                    <span className="truncate text-muted-foreground">
                      vs {formatCurrency(data!.priorGrandTotal ?? 0, currency)} prior
                    </span>
                  </span>
                ) : undefined
              }
              icon={Trophy}
              color={CHART_COLORS[0]}
            />
            <StatCard
              title="Product Families"
              value={num(data!.families.length)}
              hint="With won revenue"
              icon={Layers}
              color={CHART_COLORS[1]}
            />
            <StatCard
              title="Products"
              value={num(productCount)}
              hint="Distinct products sold"
              icon={Package}
              color={CHART_COLORS[3]}
            />
            <StatCard
              title={`Top ${concentration.topN} Concentration`}
              value={pct(concentration.shareSum)}
              hint={`Share from the top ${concentration.topN} product${concentration.topN === 1 ? '' : 's'}`}
              icon={PieChartIcon}
              color={CHART_COLORS[2]}
            />
          </div>

          {/*
            States the window rather than describing a rule. The old copy claimed it had
            excluded "the current (partial) month" while it had in fact dropped every
            month from last month's end to the end of the fiscal year.
          */}
          <p className="-mt-2 text-xs text-muted-foreground" role="status">
            {capped ? (
              <>
                Through {formatDate(endOfPrevMonth)} — complete months only. The current partial
                month is excluded; use “Include current month” to show through today.
              </>
            ) : effectiveEndDate ? (
              <>Through {formatDate(effectiveEndDate)}, including the current partial month.</>
            ) : null}
          </p>

          {comparing && data!.priorStart && data!.priorEnd && (
            <p className="-mt-2 text-xs text-muted-foreground" role="status">
              Comparing to prior period {formatDate(data!.priorStart)} –{' '}
              {formatDate(data!.priorEnd)} (equal length). Change columns are current vs. prior.
            </p>
          )}

          {/* Charts */}
          <div className="grid gap-4 md:grid-cols-2">
            {/* Revenue by family donut */}
            <Card>
              <CardHeader>
                <CardTitle>Revenue by Family</CardTitle>
                <CardDescription>Each family&rsquo;s share of total won revenue</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col items-center gap-4 md:flex-row">
                  <div
                    className="relative h-[260px] w-full flex-shrink-0 md:w-[55%]"
                    role="img"
                    aria-label={`Donut chart of revenue by product family. Total ${formatCurrency(
                      data!.grandTotal,
                      currency
                    )}. Full figures in the table below.`}
                  >
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={familyChartData}
                          cx="50%"
                          cy="50%"
                          innerRadius={58}
                          outerRadius={92}
                          paddingAngle={1}
                          dataKey="value"
                          stroke="hsl(var(--background))"
                          strokeWidth={1}
                          isAnimationActive={false}
                        >
                          {familyChartData.map((entry) => (
                            <Cell key={entry.name} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip
                          content={<ChartTooltipContent />}
                          formatter={(value: number, _name, item: any) => [
                            `${formatCurrency(value, currency)} (${(item?.payload?.share ?? 0).toFixed(1)}%)`,
                            item?.payload?.name,
                          ]}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                    <div
                      className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center"
                      aria-hidden="true"
                    >
                      <div className="text-lg font-bold leading-none">
                        {formatCurrency(data!.grandTotal, currency)}
                      </div>
                      <div className="mt-1 text-xs text-muted-foreground">total</div>
                    </div>
                  </div>
                  <div
                    className="min-w-0 flex-1 self-stretch pr-1"
                    role="table"
                    aria-label="Product families with revenue and share"
                  >
                    <div role="rowgroup">
                      {familyChartData.map((item) => (
                        <div
                          key={item.name}
                          role="row"
                          className="flex items-center gap-2 border-b border-border py-1.5 text-sm last:border-b-0"
                        >
                          <span
                            role="cell"
                            aria-hidden="true"
                            className="inline-block h-3 w-3 flex-shrink-0 rounded-sm"
                            style={{ backgroundColor: item.color }}
                          />
                          <span role="cell" className="flex-1 truncate" title={item.name}>
                            {item.name}
                          </span>
                          <span role="cell" className="tabular-nums text-muted-foreground">
                            {formatCurrency(item.value, currency)}
                          </span>
                          <span
                            role="cell"
                            className="w-12 text-right tabular-nums text-muted-foreground"
                          >
                            {pct(item.share)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Top products bar chart */}
            <Card>
              <CardHeader>
                <CardTitle>Top Products</CardTitle>
                <CardDescription>
                  Highest won revenue (top {topProducts.length}). Top {concentration.topN} carry{' '}
                  {pct(concentration.shareSum)} of total.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div
                  role="img"
                  aria-label="Horizontal bar chart of the top products by won revenue. Full figures in the table below."
                >
                  <ResponsiveContainer width="100%" height={barChartHeight}>
                    <BarChart data={topProducts} layout="vertical" margin={DEFAULT_CHART_MARGIN}>
                      <XAxis
                        type="number"
                        tickFormatter={formatChartCurrency}
                        tick={{ fontSize: 11 }}
                      />
                      <YAxis type="category" dataKey="name" width={150} tick={{ fontSize: 11 }} />
                      <Tooltip
                        content={<ChartTooltipContent />}
                        formatter={(value: number, _name, item: any) => [
                          `${formatCurrency(value, currency)} (${(item?.payload?.share ?? 0).toFixed(1)}%)`,
                          'Revenue',
                        ]}
                        cursor={{ fill: 'hsl(var(--muted))' }}
                      />
                      <Bar dataKey="revenue" radius={[0, 3, 3, 0]}>
                        {topProducts.map((entry, index) => (
                          <Cell key={`${entry.name}-${index}`} fill={entry.color} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Monthly revenue trend, stacked by family */}
          <Card>
            <CardHeader>
              <CardTitle>Revenue Trend by Family</CardTitle>
              <CardDescription>
                Monthly Closed-Won revenue over the selected range, stacked by family.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {trendQuery.isLoading ? (
                <Loading />
              ) : trendData.rows.length < 2 ? (
                <p className="py-8 text-center text-sm text-muted-foreground">
                  Not enough months in this range to plot a trend. Widen the date range to see
                  revenue over time.
                </p>
              ) : (
                <div
                  role="img"
                  aria-label={`Stacked area chart of monthly won revenue by product family across ${trendData.rows.length} months. Full figures in the table below.`}
                >
                  <ResponsiveContainer width="100%" height={340}>
                    <AreaChart data={trendData.rows} margin={DEFAULT_CHART_MARGIN}>
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke={CHART_AXIS}
                        strokeOpacity={0.2}
                      />
                      <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke={CHART_AXIS} />
                      <YAxis
                        tickFormatter={formatChartCurrency}
                        tick={{ fontSize: 11 }}
                        stroke={CHART_AXIS}
                      />
                      <Tooltip
                        content={<ChartTooltipContent />}
                        formatter={(value: number, name: string) => [
                          formatCurrency(value, currency),
                          name,
                        ]}
                      />
                      <Legend />
                      {trendData.families.map((f, index) => {
                        const color = colorByFamily.get(f) ?? familyColor(index)
                        return (
                          <Area
                            key={f}
                            type="monotone"
                            dataKey={f}
                            name={f}
                            stackId="1"
                            stroke={color}
                            fill={color}
                            fillOpacity={0.85}
                            isAnimationActive={false}
                          />
                        )
                      })}
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Detailed table */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between gap-2">
              <div>
                <CardTitle>Product Revenue</CardTitle>
                <CardDescription>
                  Won revenue attributed to each product. Products are grouped by family with a
                  subtotal; each product&rsquo;s share is of the grand total. Click a column heading
                  to sort.
                </CardDescription>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  setCollapsed(
                    allCollapsed ? new Set() : new Set(data!.families.map((f) => f.family))
                  )
                }
                aria-label={allCollapsed ? 'Expand all families' : 'Collapse all families'}
              >
                {allCollapsed ? 'Expand all' : 'Collapse all'}
              </Button>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              <Table aria-label="Product revenue by family">
                <TableHeader>
                  <TableRow>
                    <SortableHeader
                      label="Product"
                      columnKey="name"
                      activeKey={sortKey}
                      dir={sortDir}
                      onSort={handleSort}
                    />
                    <TableHead scope="col">Code</TableHead>
                    <SortableHeader
                      label="Won Deals"
                      columnKey="wonDeals"
                      activeKey={sortKey}
                      dir={sortDir}
                      onSort={handleSort}
                      align="right"
                    />
                    <SortableHeader
                      label="Units"
                      columnKey="units"
                      activeKey={sortKey}
                      dir={sortDir}
                      onSort={handleSort}
                      align="right"
                    />
                    <SortableHeader
                      label="Avg Deal"
                      columnKey="avgDeal"
                      activeKey={sortKey}
                      dir={sortDir}
                      onSort={handleSort}
                      align="right"
                    />
                    <SortableHeader
                      label="Per Unit"
                      columnKey="perUnit"
                      activeKey={sortKey}
                      dir={sortDir}
                      onSort={handleSort}
                      align="right"
                    />
                    <SortableHeader
                      label="Revenue"
                      columnKey="revenue"
                      activeKey={sortKey}
                      dir={sortDir}
                      onSort={handleSort}
                      align="right"
                    />
                    {comparing && (
                      <TableHead className="text-right" scope="col">
                        Δ vs Prior
                      </TableHead>
                    )}
                    <TableHead className="text-right" scope="col">
                      % of Total
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedFamilies.map((fam) => (
                    <FamilyGroup
                      key={fam.family}
                      family={fam}
                      color={colorByFamily.get(fam.family) ?? CHART_COLORS[0]}
                      currency={currency}
                      comparing={comparing}
                      collapsed={collapsed.has(fam.family)}
                      onToggle={() => toggleFamily(fam.family)}
                    />
                  ))}
                  {/*
                    The reconciliation row. Grand Total is won DEAL value; the family
                    rows are product line items. Those two are not the same number:
                    a deal can have no product lines at all, and nothing in the database
                    keeps a deal's amount equal to the sum of its lines. Without this row
                    the table visibly failed to add up to its own total, and the report
                    silently read lower than the dashboard by CA$220,595.63.

                    Shown for either sign — negative means the product lines add up to
                    more than the deals they belong to, which is a data problem worth
                    seeing rather than hiding.
                  */}
                  {Math.abs(data!.unattributedRevenue ?? 0) >= 0.01 && (
                    <TableRow className="text-muted-foreground">
                      <TableCell colSpan={6}>
                        {(data!.unattributedRevenue ?? 0) > 0
                          ? 'Won revenue not attributed to a product'
                          : 'Product lines in excess of deal value'}
                        {data!.unattributed?.dealsWithoutProductLines
                          ? ` — includes ${data!.unattributed.dealsWithoutProductLines} won deal${
                              data!.unattributed.dealsWithoutProductLines === 1 ? '' : 's'
                            } with no product lines`
                          : ''}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatCurrency(data!.unattributedRevenue ?? 0, currency)}
                      </TableCell>
                      {comparing && <TableCell />}
                      <TableCell className="text-right tabular-nums">
                        {data!.grandTotal
                          ? (((data!.unattributedRevenue ?? 0) / data!.grandTotal) * 100).toFixed(1)
                          : '0.0'}
                        %
                      </TableCell>
                    </TableRow>
                  )}
                  <TableRow className="border-t-2 font-semibold">
                    <TableCell colSpan={6}>Grand Total</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatCurrency(data!.grandTotal, currency)}
                    </TableCell>
                    {comparing && (
                      <TableCell className="text-right tabular-nums">
                        <Delta
                          deltaAbs={data!.grandDeltaAbs}
                          deltaPct={data!.grandDeltaPct}
                          currency={currency}
                        />
                      </TableCell>
                    )}
                    <TableCell className="text-right tabular-nums">100.0%</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  )
}

function FamilyGroup({
  family,
  color,
  currency,
  comparing,
  collapsed,
  onToggle,
}: {
  family: ProductRevenueFamily
  color: string
  currency: string
  comparing: boolean
  collapsed: boolean
  onToggle: () => void
}) {
  const deals = family.products.reduce((s, p) => s + p.wonDeals, 0)
  const units = family.products.reduce((s, p) => s + p.units, 0)
  const Chevron = collapsed ? ChevronRight : ChevronDown
  return (
    <>
      <TableRow className="bg-muted/50 font-medium">
        <TableCell colSpan={6}>
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={!collapsed}
            aria-label={`${collapsed ? 'Expand' : 'Collapse'} ${family.family} (${family.products.length} products)`}
            className="flex items-center gap-2 rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            <Chevron className="h-4 w-4 flex-shrink-0 text-muted-foreground" aria-hidden="true" />
            <span
              aria-hidden="true"
              className="inline-block h-3 w-3 flex-shrink-0 rounded-sm"
              style={{ backgroundColor: color }}
            />
            {family.family}
            <span className="text-xs font-normal text-muted-foreground">
              ({family.products.length})
            </span>
          </button>
        </TableCell>
        <TableCell className="text-right tabular-nums">
          {formatCurrency(family.subtotal, currency)}
        </TableCell>
        {comparing && (
          <TableCell className="text-right tabular-nums">
            <Delta deltaAbs={family.deltaAbs} deltaPct={family.deltaPct} currency={currency} />
          </TableCell>
        )}
        <TableCell className="text-right tabular-nums">
          {pct(family.share)}
          <ShareBar share={family.share} color={color} />
        </TableCell>
      </TableRow>
      {!collapsed &&
        family.products.map((p) => (
          <TableRow key={p.productId}>
            <TableCell className="pl-6">{p.name}</TableCell>
            <TableCell className="text-muted-foreground">{p.productCode || '—'}</TableCell>
            <TableCell className="text-right tabular-nums">{num(p.wonDeals)}</TableCell>
            <TableCell className="text-right tabular-nums">{num(p.units)}</TableCell>
            <TableCell className="text-right tabular-nums">
              {formatCurrency(avgDealOf(p.revenue, p.wonDeals), currency)}
            </TableCell>
            <TableCell className="text-right tabular-nums">
              {p.units > 0 ? formatCurrency(perUnitOf(p.revenue, p.units), currency) : '—'}
            </TableCell>
            <TableCell className="text-right tabular-nums">
              {formatCurrency(p.revenue, currency)}
            </TableCell>
            {comparing && (
              <TableCell className="text-right tabular-nums">
                <Delta deltaAbs={p.deltaAbs} deltaPct={p.deltaPct} currency={currency} />
              </TableCell>
            )}
            <TableCell className="text-right tabular-nums">
              {pct(p.share)}
              <ShareBar share={p.share} color={color} />
            </TableCell>
          </TableRow>
        ))}
      {collapsed && (
        <TableRow className="text-xs text-muted-foreground">
          <TableCell colSpan={2} className="pl-6 italic">
            {family.products.length} product{family.products.length === 1 ? '' : 's'} hidden
          </TableCell>
          <TableCell className="text-right tabular-nums">{num(deals)}</TableCell>
          <TableCell className="text-right tabular-nums">{num(units)}</TableCell>
          <TableCell colSpan={comparing ? 5 : 4} />
        </TableRow>
      )}
    </>
  )
}
